import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { UserItem, ScEra, ScProductType, ItemVisibility } from '@/types/userItem';
import { ListingCategory, ListingCondition } from '@/types/listing';
import { getBoundingBox, DistanceOption } from '@/lib/distanceUtils';
import { resizeImage } from '@/lib/imageResizeUtils';

// Removed dead `useMarketplaceItems` hook (superseded by `useMarketItems`).
// It lacked the HIDE_BETA_FROM_BUYERS filter — kept its removal as an audit fix.
import { seedSellerContactNote } from '@/lib/dialogPrefetch';
import { STORAGE_BUCKETS } from '@/lib/storageBuckets';
import { queryKeys } from '@/lib/queryKeys';
import {
  cancelItemQueries,
  patchItemInCaches,
  removeItemFromCaches,
  restoreItemCaches,
} from '@/lib/optimisticItems';



// Fetch single item
export function useUserItem(idOrShortId: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: queryKeys.userItems.byId(idOrShortId),
    queryFn: async () => {
      if (!idOrShortId) return null;

      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrShortId);
      const lookupColumn = isUuid ? 'id' : 'short_id';

      // Owners need full row (cost basis, notes, eBay config) — try base table first
      // (RLS returns the row only if the caller owns it). Fall back to public_user_items
      // view (safe columns only, listed/showcase) for buyers and anon visitors.
      const baseSelect = `
        *,
        images:user_item_images(*),
        seller:profiles!user_items_user_id_fkey(
          user_id, username, avatar_url, city, state_code, seller_rating, total_sales,
          verification_tier, identity_verified_at, created_at, social_links,
          offers_local_pickup, will_ship_items,
          profile_visibility, contact_instructions
        )
      `;

      const ownerRes = await supabase
        .from('user_items')
        .select(baseSelect)
        .eq(lookupColumn, idOrShortId)
        .maybeSingle();
      if (ownerRes.error) throw ownerRes.error;

      let data: any = ownerRes.data;
      if (!data) {
        // Buyer / anon path: route through curated view (no FKs detected by PostgREST,
        // so seller + images are fetched separately).
        const publicRes = await (supabase as any)
          .from('public_user_items')
          .select('*')
          .eq(lookupColumn, idOrShortId)
          .maybeSingle();
        if (publicRes.error) throw publicRes.error;
        if (!publicRes.data) return null;

        const [imagesRes, sellerRes] = await Promise.all([
          supabase
            .from('user_item_images')
            .select('*')
            .eq('item_id', publicRes.data.id),
          supabase
            .from('public_profiles')
            .select('user_id, username, avatar_url, city, state_code, seller_rating, total_sales, verification_tier, identity_verified_at, created_at, social_links, offers_local_pickup, will_ship_items, profile_visibility, contact_instructions')
            .eq('user_id', publicRes.data.user_id)
            .maybeSingle(),
        ]);

        data = {
          ...publicRes.data,
          images: imagesRes.data || [],
          seller: sellerRes.data || null,
        };
      }
      if (!data) return null;

      if (data?.seller?.username) {
        seedSellerContactNote(qc, data.seller.username, data.seller.contact_instructions);
      }

      return {
        ...data,
        images: data.images?.sort((a: any, b: any) => a.display_order - b.display_order) || [],
      } as UserItem & { seller: any };
    },
    enabled: !!idOrShortId,
  });
}

// Fetch current user's items (collection)
// Uses auth context for proper session sync - items refetch when auth state changes
// Trimmed column list — user_items has 143 columns but the Inventory grid,
// filters, stats, bulk actions, and eBay list use ~30. Wide-detail columns
// (ebay_description_html, notes, raw_json, per-unit cost arrays, etc.) are
// fetched by useUserItem(id) on the detail drawer, so no functional loss.
// Cuts payload ~60-70% on mobile.
const MY_ITEMS_COLUMNS = [
  'id', 'short_id', 'user_id', 'title', 'category', 'condition',
  'game', 'custom_game_name', 'language',
  'set_code', 'set_name', 'era', 'product_type',
  'visibility', 'is_beta', 'quantity',
  'asking_price_cents', 'previous_price_cents', 'price_dropped_at',
  'market_price_cents', 'market_price_confidence', 'market_price_source',
  'market_price_fetched_at', 'market_price_low_cents', 'market_price_high_cents',
  'collectr_market_price_cents', 'collectr_price_override_cents',
  'cost_basis_cents', 'individual_costs_cents',
  'stock_image_url', 'sold_at', 'sold_price_cents', 'listed_at', 'created_at', 'updated_at',
  'pending_sale_source', 'pending_sale_at', 'pending_sale_order_ref',
  'pending_sale_price_cents', 'pending_sale_quantity', 'pending_prior_visibility',
  'grading_company', 'slab_grade', 'certification_number',
  'is_negotiable', 'accepts_trades', 'has_proof_pack', 'proof_images',
  'card_number', 'finish', 'finish_label', 'rarity', 'pokemon_type', 'artist',
  'is_first_edition', 'is_reverse_holo', 'is_error_card',
  'sealed_condition', 'pack_count',
  'scrydex_match_confidence', 'scrydex_matched_at',
  'ebay_title_override',
  'city', 'state_code', 'ships_anywhere',
  // Detail fields edited by ItemFormModal — must round-trip or edits wipe them.
  'description', 'notes', 'purchase_date', 'target_price_cents', 'error_type',
  'lot_type', 'approx_card_count', 'box_count', 'lot_highlights',
  'lot_eras', 'lot_languages',
].join(', ');

export function useMyItems() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: queryKeys.userItems.mine(user?.id),
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_items')
        .select(`${MY_ITEMS_COLUMNS}, images:user_item_images(*)`)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((item: any) => ({
        ...item,
        images: item.images?.sort((a: any, b: any) => a.display_order - b.display_order) || [],
      })) as UserItem[];
    },
    enabled: !!user,
    // M5: avoid double-fetch on every page mount; mutations already
    // invalidate ['my-items'] when they change inventory.
    staleTime: 60 * 1000,
    refetchOnMount: false,
  });
}

// Fetch user's collection items for storefront (by user_id, filtered by visibility)
interface UseUserCollectionParams {
  userId?: string;
  visibility?: ItemVisibility | 'public'; // 'public' = listed items only
}

export function useUserCollectionItems(params: UseUserCollectionParams) {
  const { userId, visibility = 'public' } = params;

  return useQuery({
    queryKey: queryKeys.userItems.collection(userId, visibility),
    queryFn: async () => {
      if (!userId) return { items: [], stats: { total: 0, listed: 0 } };

      let query: any;

      if (visibility === 'public') {
        // Buyer-facing: read through public_user_items view (omits owner-private columns).
        query = (supabase as any)
          .from('public_user_items')
          .select('*, images:user_item_images(*)')
          .eq('user_id', userId)
          .eq('visibility', 'listed');
        const { HIDE_BETA_FROM_BUYERS } = await import('@/lib/betaVisibility');
        if (HIDE_BETA_FROM_BUYERS) query = query.eq('is_beta', false);
      } else {
        query = supabase
          .from('user_items')
          .select('*, images:user_item_images(*)')
          .eq('user_id', userId)
          .eq('visibility', visibility);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      const items = (data || []).map(item => ({
        ...item,
        images: item.images?.sort((a: any, b: any) => a.display_order - b.display_order) || [],
      })) as UserItem[];

      // Calculate stats
      const listed = items.filter(i => i.visibility === 'listed').length;

      return {
        items,
        stats: {
          total: items.length,
          listed,
        },
      };
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

interface CreateUserItemInput {
  title: string;
  category: ListingCategory;
  condition: string; // Accepts any condition type
  game?: string | null;
  custom_game_name?: string | null;
  language?: string;
  set_code?: string | null;
  set_name?: string | null;
  era?: ScEra | null;
  product_type?: ScProductType | null;
  notes?: string | null;
  quantity: number;
  purchase_date?: string | null;
  cost_basis_cents?: number | null;
  individual_costs_cents?: number[] | null;
  individual_dates?: string[] | null;
  is_listed: boolean;
  asking_price_cents?: number | null;
  is_negotiable: boolean;
  accepts_trades: boolean;
  description?: string | null;
  city?: string | null;
  state_code?: string | null;
  target_price_cents?: number | null;
  // Geolocation
  latitude?: number | null;
  longitude?: number | null;
  ships_anywhere?: boolean;
  // Slab-specific
  slab_grade?: number | null;
  grading_company?: string | null;
  certification_number?: string | null;
  // Sealed-specific
  sealed_condition?: string | null;
  pack_count?: number;
  has_original_packaging?: boolean;
  has_receipt?: boolean;
  // Singles card metadata
  card_number?: string | null;
  rarity?: string | null;
  finish?: string | null;
  finish_label?: string | null;
  pokemon_type?: string | null;
  artist?: string | null;
  is_first_edition?: boolean;
  is_reverse_holo?: boolean;
  // Error card (singles)
  is_error_card?: boolean;
  error_type?: string | null;
  // Visibility
  visibility?: ItemVisibility;
  // Proof Pack
  proof_images?: Record<string, string> | null;
  
  has_proof_pack?: boolean;
  proof_video_url?: string | null;
  // Lot-specific fields
  lot_type?: string | null;
  approx_card_count?: number | null;
  box_count?: string | null;
  lot_highlights?: string | null;
  lot_eras?: string[] | null;
  lot_languages?: string[] | null;
  includes_holos?: boolean;
  includes_ultras?: boolean;
  includes_slabs?: boolean;
  // Stock image
  stock_image_url?: string | null;
}

export function useCreateUserItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateUserItemInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const itemData = {
        ...input,
        user_id: user.id,
        expires_at: input.is_listed 
          ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() 
          : null,
      };

      const { data, error } = await supabase
        .from('user_items')
        .insert(itemData as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Intentionally NOT invalidating here — ItemFormModal uploads images
      // right after create, and useUploadItemImages invalidates once the
      // gallery rows exist. Invalidating early caused a flash of "Add Photo"
      // placeholders in inventory before the uploads landed.
    },

  });
}

export function useUploadItemImages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, files }: { itemId: string; files: File[] }) => {

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Append after any existing images so new uploads never collide with the
      // current hero photo's display_order.
      const { data: existingRows } = await supabase
        .from('user_item_images')
        .select('display_order')
        .eq('item_id', itemId)
        .order('display_order', { ascending: false })
        .limit(1);
      const startOrder = (existingRows?.[0]?.display_order ?? -1) + 1;

      // M10: upload + insert all photos in parallel. Order is preserved via
      // the deterministic `startOrder + i` display_order, so the gallery
      // reads back exactly as the user picked even though the network
      // operations finish out-of-order.
      const uploadedUrls = await Promise.all(
        files.map(async (file, i) => {
          const resizedFile = await resizeImage(file);
          // Use userId as first folder to match RLS policy
          const fileName = `${user.id}/${itemId}/${Date.now()}-${i}.jpg`;

          const { error: uploadError } = await supabase.storage
            .from('listing-images')
            .upload(fileName, resizedFile, { contentType: resizedFile.type, cacheControl: '31536000' });
          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('listing-images')
            .getPublicUrl(fileName);

          const { error: insertError } = await supabase
            .from('user_item_images')
            .insert({
              item_id: itemId,
              image_url: publicUrl,
              display_order: startOrder + i,
            });
          if (insertError) throw insertError;

          return publicUrl;
        })
      );

      return uploadedUrls;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketplaceItems.all });
    },
  });
}


// Reorder existing item images by setting display_order in bulk.
// Keys by row id (unique) — keying by image_url breaks when the same URL
// appears on multiple rows (e.g. duplicate uploads).
export function useReorderItemImages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      orderedImageIds,
    }: {
      itemId: string;
      orderedImageIds: string[];
    }) => {
      // First push everything to a high temp range to avoid unique-collision
      // side effects, then assign final values.
      const updateOrder = async (id: string, displayOrder: number) => {
        const { data, error } = await supabase
          .from('user_item_images')
          .update({ display_order: displayOrder })
          .eq('item_id', itemId)
          .eq('id', id)
          .select('id')
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('Photo order was not updated');
      };

      await Promise.all(orderedImageIds.map((id, idx) => updateOrder(id, 1000 + idx)));
      await Promise.all(orderedImageIds.map((id, idx) => updateOrder(id, idx)));
      return { itemId };
    },
    onSuccess: ({ itemId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll });
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.byId(itemId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.marketplaceItems.all });
    },
  });
}

// Upload proof pack images and return URL mapping
export function useUploadProofPackImages() {
  return useMutation({
    mutationFn: async ({ itemId, proofImages }: { itemId: string; proofImages: Record<string, File> }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const uploadedUrls: Record<string, string> = {};

      for (const [photoType, file] of Object.entries(proofImages)) {
        // Resize image before upload
        const resizedFile = await resizeImage(file);
        // Use userId as first folder to match RLS policy
        const fileName = `${user.id}/${itemId}/proof-${photoType}-${Date.now()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('listing-images')
          .upload(fileName, resizedFile, { contentType: resizedFile.type, cacheControl: '31536000' });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('listing-images')
          .getPublicUrl(fileName);

        uploadedUrls[photoType] = publicUrl;
      }

      return uploadedUrls;
    },
  });
}

export function useUpdateUserItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CreateUserItemInput> & { id: string }) => {
      const { data, error } = await supabase
        .from('user_items')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    // Tier 0 responsiveness: paint the edit immediately, roll back if the
    // write fails. Covers price edits, list/unlist, offers toggles, etc.
    onMutate: async ({ id, ...updates }) => {
      await cancelItemQueries(queryClient);
      return { snapshot: patchItemInCaches(queryClient, id, updates as Record<string, any>) };
    },
    onError: (_err, _vars, context) => {
      restoreItemCaches(queryClient, context?.snapshot);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.marketplaceItems.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll });
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.all });
    },
  });
}


// Duplicate an item — copies card identity only, resets price/cost/condition/proof/sale/ebay
// New row is private (draft) until the user edits it.
export function useDuplicateUserItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // 1) Load source item + images
      const { data: source, error: fetchErr } = await supabase
        .from('user_items')
        .select('*, images:user_item_images(image_url, display_order)')
        .eq('id', sourceId)
        .single();

      if (fetchErr) throw fetchErr;
      if (!source) throw new Error('Source item not found');

      const isSlab = source.category === 'slabs';

      // 2) Build identity-only payload (whitelist of fields that carry over)
      const payload: Record<string, any> = {
        user_id: user.id,
        // Identity
        title: source.title?.includes('(Duplicate)') ? source.title : `${source.title} (Duplicate)`,
        category: source.category,
        game: source.game,
        custom_game_name: source.custom_game_name,
        language: source.language,
        set_code: source.set_code,
        set_name: source.set_name,
        era: source.era,
        product_type: source.product_type,
        card_number: source.card_number,
        finish: source.finish,
        finish_label: source.finish_label,
        rarity: source.rarity,
        pokemon_type: source.pokemon_type,
        artist: source.artist,
        is_first_edition: source.is_first_edition ?? false,
        is_reverse_holo: source.is_reverse_holo ?? false,
        is_error_card: source.is_error_card ?? false,
        error_type: source.error_type,
        stock_image_url: source.stock_image_url,
        // Slab grade is identity for slabs — keep when category=slabs
        slab_grade: isSlab ? source.slab_grade : null,
        grading_company: isSlab ? source.grading_company : null,
        certification_number: null, // cert # is unique to the physical slab
        // Reset / defaults
        condition: source.condition, // condition is required (NOT NULL) — copy then user edits
        quantity: 1,
        visibility: 'private',
        is_listed: false,
        is_negotiable: false,
        accepts_trades: false,
        asking_price_cents: null,
        previous_price_cents: null,
        price_dropped_at: null,
        cost_basis_cents: null,
        individual_costs_cents: null,
        individual_dates: null,
        purchase_date: null,
        proof_images: null,
        proof_video_url: null,
        
        has_proof_pack: false,
        has_receipt: false,
        has_original_packaging: false,
        sold_to_user_id: null,
        sold_at: null,
        listed_at: null,
        expires_at: null,
        // eBay fields cleared
        ebay_best_offer_enabled: false,
        ebay_auto_accept_cents: null,
        ebay_auto_decline_cents: null,
        ebay_list_quantity: null,
        ebay_description_html: null,
        ebay_description_source: null,
        // Import metadata cleared
        import_source: null,
        collectr_synced_at: null,
      };

      // 3) Insert new item
      const { data: newItem, error: insertErr } = await supabase
        .from('user_items')
        .insert(payload as any)
        .select()
        .single();

      if (insertErr) throw insertErr;

      // 4) Clone image refs (same URLs — no storage copy needed; bucket is public)
      const sourceImages = (source.images || []) as { image_url: string; display_order: number }[];
      if (sourceImages.length > 0) {
        const imageRows = sourceImages.map((img) => ({
          item_id: newItem.id,
          image_url: img.image_url,
          display_order: img.display_order,
        }));
        const { error: imgErr } = await supabase
          .from('user_item_images')
          .insert(imageRows);
        if (imgErr) {
          // Don't fail the whole operation — item exists, images can be re-added
          console.warn('Failed to clone images for duplicated item:', imgErr);
        }
      }

      return newItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll });
    },
  });
}

export function useDeleteUserItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('user_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await cancelItemQueries(queryClient);
      return { snapshot: removeItemFromCaches(queryClient, id) };
    },
    onError: (_err, _id, context) => {
      restoreItemCaches(queryClient, context?.snapshot);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.marketplaceItems.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll });
    },

  });
}

export function useDeleteUserItemImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string) => {
      // First get the image to find its URL for storage deletion
      const { data: image, error: fetchError } = await supabase
        .from('user_item_images')
        .select('image_url, item_id')
        .eq('id', imageId)
        .single();

      if (fetchError) throw fetchError;

      // Delete from database
      const { error: deleteError } = await supabase
        .from('user_item_images')
        .delete()
        .eq('id', imageId);

      if (deleteError) throw deleteError;

      // If this image was set as the proof-of-possession photo, clear that key
      if (image?.item_id && image?.image_url) {
        const { data: parent } = await supabase
          .from('user_items')
          .select('proof_images')
          .eq('id', image.item_id)
          .maybeSingle();
        const proof = (parent?.proof_images as Record<string, string> | null) || null;
        if (proof && proof.possession === image.image_url) {
          const { possession: _omit, ...rest } = proof;
          await supabase
            .from('user_items')
            .update({
              proof_images: Object.keys(rest).length ? rest : null,
              has_proof_pack: Object.keys(rest).length >= 3,
            })
            .eq('id', image.item_id);
        }
      }

      // Try to delete from storage (extract path from URL)
      if (image?.image_url) {
        try {
          const url = new URL(image.image_url);
          const pathMatch = url.pathname.match(/\/storage\/v1\/object\/public\/listing-images\/(.+)/);
          if (pathMatch?.[1]) {
            await supabase.storage.from(STORAGE_BUCKETS.listingImages).remove([decodeURIComponent(pathMatch[1])]);
          }
        } catch (e) {
          console.warn('Failed to delete image from storage:', e);
        }
      }

      return image?.item_id;
    },
    onSuccess: (itemId) => {
      if (itemId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.userItems.byId(itemId) });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll });
    },
  });
}
